import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useDashboardStore } from '../store/dashboardStore';

interface DashboardItemProps {
  id: string;
  children: React.ReactNode;
  className?: string;
}

export function DashboardItem({ id, children, className = '' }: DashboardItemProps) {
  const { isEditMode, isItemHidden, toggleItemVisibility } = useDashboardStore();

  // iOS Safari-like animation variants
  const variants = {
    visible: {
      opacity: 1,
      scale: 1,
      height: 'auto',
      marginTop: '1rem',
      marginBottom: '1rem',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        opacity: { duration: 0.2 }
      }
    },
    hidden: {
      opacity: 0,
      scale: 0.98,
      height: 0,
      marginTop: 0,
      marginBottom: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        opacity: { duration: 0.2 }
      }
    }
  };

  // Check if this item should be immune to hide/unhide
  const isImmune = id === 'activities';

  return (
    <AnimatePresence initial={false} mode="wait">
      {(!isItemHidden(id) || isEditMode) && (
        <motion.div
          layout
          initial={false}
          animate={isItemHidden(id) && !isEditMode ? 'hidden' : 'visible'}
          exit="hidden"
          variants={variants}
          className={`relative group ${className}`}
        >
          {isEditMode && !isImmune && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={() => toggleItemVisibility(id)}
              className={`absolute top-2 right-2 p-2 rounded-lg z-10 transition-colors ${
                isItemHidden(id)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background/80 backdrop-blur-sm text-muted-foreground hover:bg-background/90'
              }`}
            >
              {isItemHidden(id) ? (
                <Eye className="w-4 h-4" />
              ) : (
                <EyeOff className="w-4 h-4" />
              )}
            </motion.button>
          )}
          
          <motion.div
            animate={{
              opacity: isEditMode ? (isItemHidden(id) ? 0.5 : 0.8) : 1,
              scale: isEditMode ? 0.99 : 1,
              filter: isEditMode ? 'grayscale(20%)' : 'none'
            }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30
            }}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}